/**
 * Shared Payments list page — works for owner_builder, architect, and
 * platform_admin. Each role's API base path is passed in via `apiPrefix`.
 *
 * Owner   →  /api/owner-portal/payments       (spent)
 * Architect → /api/architect-portal/payments  (received, net of platform fee)
 * Admin   →  /api/admin-portal/payments       (platform-wide revenue)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { api } from "../lib/api";
import EmptyState from "../components/EmptyState";
import {
  CreditCard,
  ArrowUpRight,
  Filter,
  CheckCircle2,
} from "lucide-react";

const KIND_LABEL = {
  consultation: "Consultation",
  plan_purchase: "Construction plans",
};

function money(amount, currency = "USD") {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function dateLabel(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso.slice(0, 10); }
}

const COPY = {
  owner_builder: {
    eyebrow: "Payments & receipts",
    title: "Your payment history.",
    dek: "Every consultation deposit and plan purchase made through the platform — itemised, receipted, and reconciled with your projects.",
    statLabels: ["Lifetime spend", "Transactions", "Settled"],
    statKeys: ["lifetime_total", "count", "succeeded"],
    counterpartyHeader: "Reference",
    rowLinkPrefix: "/owner/payments",
  },
  architect: {
    eyebrow: "Receivables",
    title: "Payments received.",
    dek: "Consultations and plan purchases your firm has been paid for. Platform handles billing, escrow, and split-disbursement.",
    statLabels: ["Gross receipts", "Platform fee", "Net to firm"],
    statKeys: ["lifetime_gross", "platform_fees", "lifetime_net"],
    counterpartyHeader: "Buyer",
    rowLinkPrefix: "/architect/payments",
  },
  platform_admin: {
    eyebrow: "Platform revenue",
    title: "Fee revenue report.",
    dek: "Every transaction processed through the platform, with the 5% platform fee surfaced as recognised revenue.",
    statLabels: ["GMV", "Platform revenue", "Transactions"],
    statKeys: ["gmv", "platform_revenue", "count"],
    counterpartyHeader: "Buyer",
    rowLinkPrefix: "/admin/payments",
  },
};

export default function PaymentsPage({ apiPrefix, role }) {
  const { user } = useRole();
  const effectiveRole = role || user?.role;
  const copy = COPY[effectiveRole] || COPY.owner_builder;

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!user?.id) return;
    setData(null);
    api
      .get(`${apiPrefix}/payments?user_id=${user.id}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e));
  }, [user?.id, apiPrefix]);

  if (!user?.id) return null;

  const items = data?.items || [];
  const filtered = items.filter(
    (i) => filter === "all" || i.kind === filter,
  );
  const stats = data?.stats || {};

  return (
    <section className="animate-fade-in-up" data-testid="payments-page">
      {/* Hero */}
      <header
        className="grid lg:grid-cols-[1fr_auto] gap-10 items-end"
        style={{ paddingBottom: 28 }}
      >
        <div>
          <div className="flex items-center gap-3">
            <span aria-hidden style={{
              display: "inline-block", width: 28, height: 2, background: "#B14E2F",
            }} />
            <span className="wn-label" style={{ color: "#8F3C23" }}>
              {copy.eyebrow}
            </span>
          </div>
          <h1
            className="mt-4"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(36px, 4.6vw, 52px)",
              fontWeight: 500, lineHeight: 1.06, letterSpacing: "-0.01em",
              color: "#1B1F4B", margin: 0,
            }}
          >
            {copy.title}
          </h1>
          <p className="mt-4" style={{
            fontFamily: '"Newsreader", Georgia, serif', fontStyle: "italic",
            color: "#6C6B85", fontSize: 18, lineHeight: 1.55,
            maxWidth: 620, margin: 0,
          }}>
            {copy.dek}
          </p>
        </div>
        <aside className="hidden lg:block" style={{
          borderLeft: "1px solid #D9D8E4", paddingLeft: 20, minWidth: 200,
        }}>
          <div className="wn-label" style={{ color: "#6C6B85" }}>Statement</div>
          <div className="mt-1 tabular-nums" style={{
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 700, color: "#1B1F4B", fontSize: 15,
            letterSpacing: "0.02em",
          }}>Volume 01</div>
          <div className="mt-3" style={{
            fontFamily: '"Newsreader", Georgia, serif', fontStyle: "italic",
            color: "#6C6B85", fontSize: 13, lineHeight: 1.45,
          }}>
            All amounts in USD. Refunds processed within 5 business days.
          </div>
        </aside>
      </header>

      {/* Stats row */}
      <div className="grid grid-cols-3" style={{
        borderTop: "1px solid #D9D8E4", borderBottom: "1px solid #D9D8E4",
        padding: "22px 0", marginBottom: 32,
      }}>
        {copy.statLabels.map((label, i) => {
          const k = copy.statKeys[i];
          const v = stats[k];
          const isMoney = ["lifetime_total", "lifetime_gross",
                           "platform_fees", "lifetime_net",
                           "gmv", "platform_revenue"].includes(k);
          return (
            <Stat
              key={k}
              label={label}
              value={isMoney ? money(v ?? 0) : (v ?? 0)}
              accent={i === 0}
            />
          );
        })}
      </div>

      {/* Filter chips */}
      <div className="flex items-center justify-between" style={{ marginBottom: 18 }}>
        <div className="flex items-center gap-3">
          <Filter size={14} color="#6C6B85" strokeWidth={1.6} />
          <FilterChip
            active={filter === "all"}
            onClick={() => setFilter("all")}
            label="All" count={items.length}
          />
          <FilterChip
            active={filter === "consultation"}
            onClick={() => setFilter("consultation")}
            label="Consultations"
            count={items.filter((i) => i.kind === "consultation").length}
          />
          <FilterChip
            active={filter === "plan_purchase"}
            onClick={() => setFilter("plan_purchase")}
            label="Plan purchases"
            count={items.filter((i) => i.kind === "plan_purchase").length}
          />
        </div>
        <div className="wn-label flex items-center gap-2" style={{ color: "#4F6149" }}>
          <CheckCircle2 size={13} strokeWidth={1.8} />
          Stripe-secured
        </div>
      </div>

      {/* Table */}
      {err && (
        <EmptyState title="Could not load payments."
          body="Refresh the page or try again later." />
      )}
      {!err && data === null && (
        <div style={{ color: "#6C6B85", fontStyle: "italic" }}>
          Loading payments…
        </div>
      )}
      {!err && data && filtered.length === 0 && (
        <EmptyState title="No payments yet."
          body={
            effectiveRole === "owner_builder"
              ? "When you book a consultation or purchase plans, the receipt will land here."
              : effectiveRole === "architect"
              ? "When a client pays for a consultation or your plan-set, the receipt will appear here."
              : "When transactions are processed through the platform, they will appear here."
          }
        />
      )}
      {!err && filtered.length > 0 && (
        <div data-testid="payments-table" style={{
          border: "1px solid #D9D8E4", borderRadius: 4, overflow: "hidden",
        }}>
          {/* Header row */}
          <div className="grid" style={{
            gridTemplateColumns: "1.4fr 1.4fr 0.9fr 1.1fr 0.9fr auto",
            gap: 16, padding: "12px 20px",
            backgroundColor: "#F5F4FA",
            borderBottom: "1px solid #D9D8E4",
          }}>
            {["Description", copy.counterpartyHeader, "Method",
              "Date", "Amount", ""].map((h, i) => (
              <div key={i} className="wn-label" style={{
                color: "#6C6B85",
                textAlign: i === 4 ? "right" : "left",
              }}>{h}</div>
            ))}
          </div>
          {filtered.map((p, i) => (
            <Link
              key={p.id}
              to={`${copy.rowLinkPrefix}/${p.id}`}
              data-testid={`payment-row-${p.id}`}
              className="grid wn-payment-row"
              style={{
                gridTemplateColumns: "1.4fr 1.4fr 0.9fr 1.1fr 0.9fr auto",
                gap: 16, alignItems: "center",
                padding: "16px 20px",
                borderTop: i === 0 ? "none" : "1px solid #ECEBF1",
                textDecoration: "none", color: "inherit",
                transition: "background-color 160ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#FBFAFD"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="wn-label" style={{ color: "#8F3C23" }}>
                  {KIND_LABEL[p.kind] || p.label}
                </div>
                <div style={{
                  fontFamily: '"Fraunces", Georgia, serif', fontWeight: 500,
                  fontSize: 16, color: "#1B1F4B", marginTop: 2, lineHeight: 1.2,
                }}>
                  {p.description}
                </div>
              </div>
              <div style={{
                fontFamily: '"Nunito Sans", sans-serif',
                color: "#272636", fontSize: 13, fontWeight: 600,
              }}>
                {effectiveRole === "owner_builder"
                  ? <span className="tabular-nums" style={{ letterSpacing: "0.01em" }}>{p.id}</span>
                  : (p.owner?.organization || p.owner?.name || p.id)}
              </div>
              <div className="flex items-center gap-2" style={{ color: "#272636", fontSize: 13 }}>
                <CreditCard size={14} color="#6C6B85" strokeWidth={1.6} />
                <span className="tabular-nums">•••• {p.last4 || "4242"}</span>
              </div>
              <div className="tabular-nums" style={{ color: "#272636", fontSize: 13 }}>
                {dateLabel(p.paid_at)}
              </div>
              <div className="tabular-nums" style={{
                textAlign: "right", fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700, fontSize: 16, color: "#1B1F4B",
              }}>
                {money(p.amount, p.currency)}
              </div>
              <div>
                <ArrowUpRight size={16} color="#8F3C23" strokeWidth={2} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="tabular-nums" style={{
        fontFamily: '"Nunito Sans", sans-serif', fontWeight: 700,
        fontSize: 32, color: accent ? "#8F3C23" : "#1B1F4B",
        lineHeight: 1, letterSpacing: "-0.01em",
      }}>
        {value}
      </div>
      <div className="wn-label mt-2" style={{ color: "#6C6B85" }}>
        {label}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, count }) {
  return (
    <button type="button" onClick={onClick}
      data-testid={`payments-filter-${label.toLowerCase().replace(/\s+/g, "-")}`}
      style={{
        padding: "6px 12px", borderRadius: 999,
        border: `1px solid ${active ? "#1B1F4B" : "#D9D8E4"}`,
        background: active ? "#1B1F4B" : "transparent",
        color: active ? "#FBFAFD" : "#272636",
        fontFamily: '"Nunito Sans", sans-serif',
        fontSize: 12.5, fontWeight: 600, letterSpacing: "0.01em",
        cursor: "pointer",
        transition: "background-color 140ms ease, border-color 140ms ease",
      }}>
      {label}
      <span className="tabular-nums" style={{ marginLeft: 6, opacity: 0.8 }}>
        {count}
      </span>
    </button>
  );
}
